import { Button, Warning } from '@core/ui';
import { Link } from '@online-webinar/ui-frontend';

import { WebinarDataWithStatus } from '@core/swagger-client';
import { WebinarStatus } from '@core/types';
import { useContext, useEffect, useState } from 'react';
import { CanceledWebinarMessage } from '../../atoms';

import { AuthContext } from '@core/shared';
import { Trans, useTranslation } from 'react-i18next';

interface WebinarViewerActionsProps {
    subscribe: () => void;
    unsubscribe: () => void;
    webinar: WebinarDataWithStatus;
    isLoading: boolean;
}

const secondsInMinute = 60;
const secondsInHour = 60 * secondsInMinute;
const secondsInDay = 24 * secondsInHour;

export const WebinarViewerActions = ({ subscribe, unsubscribe, webinar, isLoading }: WebinarViewerActionsProps) => {
    const [watchCountdownText, setWatchCountdownText] = useState('');
    const [watchCountdown, setWatchCountdown] = useState(webinar.canWatchCountdown);

    const { isVerified, isAuthenticated } = useContext(AuthContext);

    const { t } = useTranslation();

    useEffect(() => {
        if (watchCountdown > secondsInDay) {
            const days = (watchCountdown - (watchCountdown % secondsInDay)) / secondsInDay;
            setWatchCountdownText(`${days} ${t('Generic.Days')}`);
        } else if (watchCountdown > secondsInHour) {
            const hours = (watchCountdown - (watchCountdown % secondsInHour)) / secondsInHour;
            setWatchCountdownText(`${hours} ${t('Generic.Hour.Full')}`);
        } else if (watchCountdown > secondsInMinute * 15) {
            const minutes = (watchCountdown - (watchCountdown % secondsInMinute)) / secondsInMinute;
            setWatchCountdownText(`${minutes} ${t('Generic.Minute.Full')}`);
        } else if (watchCountdown > secondsInMinute * 2) {
            const minutes = (watchCountdown - (watchCountdown % secondsInMinute)) / secondsInMinute;
            const seconds = watchCountdown % secondsInMinute;
            setWatchCountdownText(`${minutes} ${t('Generic.Minute.Full')} ${t('Generic.And')} ${seconds} ${t('Generic.Second.Full')}`);
        } else {
            setWatchCountdownText(`${watchCountdown} ${t('Generic.Second.Full')}`);
        }

        setTimeout(() => setWatchCountdown(watchCountdown - 1), 1000);
    }, [watchCountdown, webinar]);

    let watchLink: '/$webinarId/watch' | '/$webinarId/on-demand' | '/$webinarId/a/{-$authorizationCode}' | '' = '/$webinarId/watch';
    if (webinar.canViewAnonymously) {
        watchLink = `/$webinarId/a/{-$authorizationCode}`;
    } else if (webinar.isOnDemand) {
        watchLink = `/$webinarId/on-demand`;
    }

    return (
        <div className="sticky bottom-0 bg-accent-lighter rounded-webinarsub flex flex-col sm:flex-row gap-4 px-4 md:px-10 pt-2 pb-4 z-30">
            {!webinar.canViewAnonymously && !webinar.isInterested && webinar.canShowInterest && (
                <Button type="button" variant="success" onClick={() => subscribe()} isLoading={isLoading} data-testid="subscribe-to-webinar-button">
                    {t('WebinarDetails.Button.Interested')}
                </Button>
            )}

            {!webinar.canViewAnonymously && webinar.isInterested && webinar.canShowInterest && (
                <Button type="button" variant="link" onClick={() => unsubscribe()} isLoading={isLoading} data-testid="unsubscribe-webinar-button">
                    {t('WebinarDetails.Button.NoLongerInterested')}
                </Button>
            )}

            {webinar.canWatch && (
                <Link to={watchLink} params={{ webinarId: webinar.webinarId }} variant="primary" disabled={isLoading} data-testid="watch-webinar-link">
                    {watchCountdown > 0 ? (
                        <Trans i18nKey="WebinarDetails.Button.WatchIn" values={{ watchCountdown: watchCountdownText }} />
                    ) : (
                        <Trans i18nKey="Generic.Button.Watch" />
                    )}
                </Link>
            )}

            {webinar.isRestricted && !isAuthenticated && (
                <Link to="/$webinarId/login" params={{ webinarId: webinar.webinarId }} variant="primary" disabled={isLoading}>
                    {t('Account.Button.Login')}
                </Link>
            )}

            {webinar.isRestricted && isAuthenticated && (
                <div className="flex items-center mt-2" data-testid="webinar-is-cancelled-message">
                    <Warning classNames="w-6 mr-2" />
                    <span className="text-sm">{t('WebinarDetails.Warning.AccessRestricted')}</span>
                </div>
            )}

            {webinar.requiresAccountVerification && isAuthenticated && !isVerified && (
                <Link variant="primary" disabled={true}>
                    {t('WebinarDetails.Button.VerifyToWatch')}
                </Link>
            )}

            {webinar.status === WebinarStatus.Cancelled && (
                <div className="flex items-center mt-2" data-testid="webinar-is-cancelled-message">
                    <Warning classNames="w-6 mr-2" />
                    <CanceledWebinarMessage />
                </div>
            )}
        </div>
    );
};
